import translate from 'src/common/helpers/language/translate'
import globalTranslateObject from 'src/translate/global.translate'
import translationObject from './translate/baseBranchAvailability.translate'
import { onBeforeMount, reactive, watch } from 'vue'
import {
  IBaseBranchAvailabilityHookArgs,
  IBaseBranchAvailabilityStateReactive,
} from './interfaces/baseBranchAvailability.interface'
import { useBranchesStore } from 'src/stores'

export default function useBaseBranchAvailabilityHook({ props, model }: IBaseBranchAvailabilityHookArgs) {
  /* -------------------------- Translation Variables ------------------------- */

  const { t } = translate(translationObject)
  const { t: globalT } = translate(globalTranslateObject)

  /* -------------------------------- Constants ------------------------------- */

  const branchesStore = useBranchesStore()

  const state = reactive<IBaseBranchAvailabilityStateReactive>({
    rows: [],
    availabilities: [],
    availabilityDialog: false,
    availabilityDetails: null,
  })

  const columns = [
    {
      label: globalT('branch_name'),
      align: 'left',
      field: 'storeName',
      name: 'storeName',
      slot: 'name',
      headerClasses: 'table-column-fit-content',
      style: 'width: 260px',
      cellClasses: 'overflow-hidden ellipsis block',
    },
    {
      label: globalT('branch_id'),
      align: 'left',
      field: 'storeId',
      name: 'storeId',
      headerClasses: 'table-column-fit-content',
    },
    {
      label: t('availability_time'),
      align: 'left',
      field: 'availabilityId',
      name: 'availabilityId',
      slot: 'availability',
      style: 'width: 290px',
    },
  ]

  /* -------------------------------- Functions ------------------------------- */

  function generateTableRows() {
    const stores = [...branchesStore.all_stores]
    for (const store of stores) {
      const row = { storeName: store.label, storeId: Number(store.value), availabilityId: 0, active: false }
      if (model.value[row.storeId]) {
        const availability = state.availabilities.find(
          (availability) => availability.value === model.value[row.storeId],
        )
        if (availability) row.availabilityId = Number(availability.value)
        else if (state.availabilities.length) row.availabilityId = Number(state.availabilities[0].value)
        row.active = true
      } else if (state.availabilities.length) row.availabilityId = Number(state.availabilities[0].value)
      state.rows.push(row)
    }
  }

  function generateAvailabilityData() {
    state.availabilities = []
    if (props.availabilities) {
      for (const availability of props.availabilities) {
        const availabilityOption = { label: availability.name, value: availability.id }
        state.availabilities.push(availabilityOption)
      }
    }
  }

  function generateOutput() {
    const output: Record<number, number> = {}
    for (const row of state.rows) if (row.active) output[row.storeId] = row.availabilityId
    model.value = output
  }

  function showAvailabilityDetails(availabilityId: number) {
    if (props.availabilities) {
      state.availabilityDetails =
        props.availabilities.find((availability) => availability.id === availabilityId) ?? null
      state.availabilityDialog = true
    }
  }

  /* -------------------------------- Watchers -------------------------------- */

  onBeforeMount(() => {
    generateAvailabilityData()
    generateTableRows()
  })

  watch(
    () => state.rows,
    () => {
      generateOutput()
    },
    { deep: true },
  )

  return { state, columns, t, globalT, showAvailabilityDetails }
}
